import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ReferentielEnum } from 'src/app/utils/enums/referentiel-enum';
import { Referentiel } from 'src/app/utils/interfaces/new/referentiel.interface';
import { environment } from 'src/environments/environment';
import { SnackbarService } from '../snackbar/snackbar.service';
import { AldReferentiel } from './specific/ald.referentiel';
import{ AldReferentielEnrichi } from 'src/app/utils/interfaces/new/ald-referentiel-enrichi';

@Injectable({
  providedIn: 'root'
})
export class ReferentielService {

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders()
  };

  private apiUri = environment.basePath + '/user/';
  private apiHypermedUri = environment.basePath + '/hypermed/';

  constructor(
    private _http: HttpClient,
    private _snack: SnackbarService
  ) { }

  async findReferentielsList(): Promise<Referentiel[]> {
    try {
      const res = await this._http.get(this.apiHypermedUri + 'referentiels', this.httpOptions).toPromise();
      const refs = res.body as Referentiel[];
      return refs;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findRefListTable(nomTable: string): Promise<Referentiel[]> {
    try {
      const referentielsCache = ReferentielCache.getReferentielFromCache(EnumReferentielCacheType.USER, nomTable);
      if (referentielsCache) {
        return referentielsCache;
      }

      const res = await this._http.get(this.apiUri + 'referentiel/' + nomTable, this.httpOptions).toPromise();
      const refs = res.body as Referentiel[];
      const editedRefs = this.specificTableActions(nomTable, refs);

      ReferentielCache.setReferentielToCache(EnumReferentielCacheType.USER, nomTable, editedRefs);
      return editedRefs;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async getAldReferentielEnrichi(): Promise<AldReferentielEnrichi[]> {
    try {

      const res = await this._http.get<AldReferentielEnrichi[]>(this.apiUri + 'referentiel/ald-referentiel-enrichi', this.httpOptions).toPromise();
      const refs = res.body as AldReferentielEnrichi[];
      return refs;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findRefListTableHypermed(nomTable: string, recherche?: string): Promise<Referentiel[]> {
    try {
      const referentielsCache = ReferentielCache.getReferentielFromCache(EnumReferentielCacheType.HYPERMED, nomTable);
      if (referentielsCache && !recherche) {
        return referentielsCache;
      }

      const options = {...this.httpOptions};
      if (recherche && recherche !== '') {
        options['params'] = { recherche }
      }
      const res = await this._http.get(this.apiHypermedUri + 'referentiel/' + nomTable, options).toPromise();
      const refs = res.body as Referentiel[];

      if (!recherche) {
        ReferentielCache.setReferentielToCache(EnumReferentielCacheType.HYPERMED, nomTable, refs);
      }

      const editedRefs = this.specificTableActions(nomTable, refs);
      return editedRefs;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findTerminologie(nomTerm: string, searchByCode?: string, searchByLibelle?: string): Promise<Referentiel[]> {
    try {
      const options = {...this.httpOptions};
      if (searchByCode && searchByCode !== '') {
        options['params'] = { searchCode: searchByCode };
      }
      if (searchByLibelle && searchByLibelle !== '') {
        options['params'] = { searchLibelle: searchByLibelle };
      }
      const res = await this._http.get(this.apiHypermedUri + 'terminologies/' + nomTerm, options).toPromise();
      const refs = res.body as Referentiel[];
      const editedRefs = this.specificTableActions(nomTerm, refs);
      return editedRefs;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async addRefOneToUser(nomTable: string, referentiel: Referentiel) {
    try {
      ReferentielCache.clearReferentielCacheForTable(EnumReferentielCacheType.USER, nomTable);
      return await this._http.post(this.apiUri + 'referentiel/' + nomTable, referentiel, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async syncRefToUser(nomTable: string) {
    try {
      ReferentielCache.clearReferentielCacheForTable(EnumReferentielCacheType.USER, nomTable);
      return await this._http.post(this.apiUri + 'referentiel/' + nomTable + '/sync/', this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async syncRefOneToUser(nomTable: string, idReferentiel: string) {
    try {
      ReferentielCache.clearReferentielCacheForTable(EnumReferentielCacheType.USER, nomTable);
      return await this._http.post(this.apiUri + 'referentiel/' + nomTable + '/sync/' + idReferentiel, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updateRefOneToUser(nomTable: string, ref: Referentiel) {
    try {
      ReferentielCache.clearReferentielCacheForTable(EnumReferentielCacheType.USER, nomTable);
      return await this._http.put(this.apiUri + 'referentiel/' + nomTable + '/referentiel/' + ref.id, ref, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async deleteRefOneToUser(nomTable: string, idReferentiel: string) {
    try {
      ReferentielCache.clearReferentielCacheForTable(EnumReferentielCacheType.USER, nomTable);
      return await this._http.delete(this.apiUri + 'referentiel/' + nomTable + '/referentiel/' + idReferentiel, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  specificTableActions(nomTable: string, data: Referentiel[]): Referentiel[] {
    switch (nomTable) {
      case ReferentielEnum.pathologie:
        return AldReferentiel.sort(data);
      case ReferentielEnum.ald:
        return AldReferentiel.sort(data);
      default:
        return data;
    }
  }
}


export class ReferentielCache {
  private static _referentielCache: Map<EnumReferentielCacheType, Map<string, Array<Referentiel>>> = new Map();

  static setReferentielToCache(type: EnumReferentielCacheType, nomTable: string, referentiel: Array<Referentiel>) {
    let referentiels = this._referentielCache.get(type);
    if (!referentiels) {
      referentiels = new Map<string, Array<Referentiel>>();
      this._referentielCache.set(type, referentiels);
    }
    referentiels.set(nomTable, referentiel);
  }

  static getReferentielFromCache(type: EnumReferentielCacheType, nomTable: string): Array<Referentiel> {
    const referentiels = this._referentielCache.get(type);
    if (referentiels) {
      return referentiels.get(nomTable);
    } else {
      return undefined;
    }
  }

  static clearReferentielCacheForTable(type: EnumReferentielCacheType, nomTable: string) {
    const referentiels = this._referentielCache.get(type);
    if (referentiels) {
      return referentiels.delete(nomTable);
    }
  }

  static clearReferentielType(type: EnumReferentielCacheType) {
    this._referentielCache.delete(type);
  }

  static clearAllReferentiel() {
    this._referentielCache.clear();
  }
}

enum EnumReferentielCacheType {
  USER = 'USER',
  HYPERMED = 'HYPERMED',
}
